import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "pwa_install_dismissed_until";
const DISMISS_DAYS = 7;

function isRunningStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isDismissed() {
  const until = localStorage.getItem(DISMISS_KEY);
  if (!until) return false;
  return Date.now() < parseInt(until, 10);
}

export default function PWAInstallBanner() {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState(null); // "native" | "ios"
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isRunningStandalone() || isDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode("native");
      setTimeout(() => setShow(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS: no beforeinstallprompt, detect manually
    if (isIOS()) {
      setTimeout(() => {
        setMode("ios");
        setShow(true);
      }, 3000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 864e5));
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  if (!show || !mode) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-md mx-auto bg-white border border-border rounded-2xl shadow-xl p-4 flex gap-3 items-start">
        {/* App icon */}
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))] grid place-items-center shrink-0">
          <span className="text-white font-display font-bold text-lg leading-none">O</span>
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-zinc-900">Instala la app de OFERTAS</p>
          {mode === "native" && (
            <>
              <p className="text-xs text-muted-foreground mt-0.5">
                Acceso rápido desde tu pantalla de inicio. Sin App Store.
              </p>
              <Button
                onClick={install}
                size="sm"
                className="mt-2 btn-primary rounded-full text-xs h-8 px-4"
                data-testid="pwa-install-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Instalar gratis
              </Button>
            </>
          )}
          {mode === "ios" && (
            <>
              <p className="text-xs text-muted-foreground mt-0.5">
                Toca{" "}
                <span className="inline-flex items-center gap-0.5 font-medium text-zinc-700">
                  <Share className="w-3 h-3" /> Compartir
                </span>{" "}
                y luego <strong>"Agregar a pantalla de inicio"</strong>.
              </p>
            </>
          )}
        </div>

        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-zinc-700 transition-colors shrink-0 mt-0.5"
          aria-label="Cerrar"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
